<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mapOptionMultiselect } from "@/utils/helper";
import { usePositionStore } from "@/stores/master/position";
import { CascadingSchemePayload } from "@/types/interface/cascading-scheme";
import { useCascadingSchemeStore } from "@/stores/management/cascading-scheme";
import { useCategoryGroupStore } from "@/stores/master/category-group-store";

const idCascadingScheme = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const groupCategoryStore = useCategoryGroupStore();
const positionStore = usePositionStore();
const cascadingSchemeStore = useCascadingSchemeStore();

const listPosition = ref<any>();
const listGroupCategory = ref<any>();
const detailCascadingScheme = ref<any>();

const validationSchema = toTypedSchema(
  zod.object({
    group_category: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    submission: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    approval: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    step: zod.number(),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: group_category } = useField<number>("group_category");
const { value: submission } = useField<number>("submission");
const { value: approval } = useField<number>("approval");
const { value: step } = useField<number>("step");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadCascadingScheme: CascadingSchemePayload = {
    group_category_id: group_category.value?.value,
    submission: submission.value?.value,
    approval: approval.value?.value,
    step_number: step.value,
  };

  const responseApi = ref<any>();
  if (isEdit.value == true) {
    responseApi.value = await cascadingSchemeStore.dispatchUpdate(
      idCascadingScheme.value,
      payloadCascadingScheme
    );
  } else {
    responseApi.value = await cascadingSchemeStore.dispatchCreate(
      payloadCascadingScheme
    );
  }
  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value
          ? "Cascading Scheme Successfully Updated"
          : "Cascading Scheme Successfully Added"
      );
      router.push("/management/cascading-scheme");
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadListPosition = async () => {
  const { success, message } = await positionStore.dispatchGetPositions();
  if (success) {
    listPosition.value = mapOptionMultiselect(
      await positionStore?.positions?.items
    );
  }
};

const loadListGroupCategory = async () => {
  const { success, message } =
    await groupCategoryStore.dispatchGetCategoryGroups();
  if (success) {
    listGroupCategory.value = mapOptionMultiselect(
      await groupCategoryStore.categoryGroups.items
    );
  }
};

const loadDetailCascadingScheme = async () => {
  const { success, message } = await cascadingSchemeStore.dispatchDetail(
    idCascadingScheme.value
  );
  if (success) {
    detailCascadingScheme.value = cascadingSchemeStore.scheme?.data;
    group_category.value = mapOptionMultiselect(
      detailCascadingScheme.value?.groupCategory
    );
    submission.value = mapOptionMultiselect(
      detailCascadingScheme.value?.submission
    );
    approval.value = mapOptionMultiselect(
      detailCascadingScheme.value?.approval
    );
  }
};

onMounted(() => {
  loadListPosition();
  loadListGroupCategory();
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idCascadingScheme.value = router.currentRoute.value.params.id.toString();
    loadDetailCascadingScheme();
  }
});
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Cascading Scheme</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/management/cascading-scheme"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Cascading Scheme
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="group_category"
                    class="form-label label text-secondary"
                    >Group Category Store Name
                    <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="group_category"
                      :options="listGroupCategory"
                      placeholder="Choose Group Category"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.group_category }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label
                    for="submission"
                    class="form-label label text-secondary"
                    >Submission Rank <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="submission"
                      :options="listPosition"
                      placeholder="Choose Submission Rank"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.submission }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="approval" class="form-label label text-secondary"
                    >Approval Rank <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="approval"
                      :options="listPosition"
                      placeholder="Choose Approval Rank"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.approval }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="step" class="form-label label text-secondary"
                    >Step Number / Level
                    <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <input
                      name="step"
                      v-model="step"
                      placeholder="Step Nummber / Level"
                      type="number"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.step }}</span>
                </div>
              </div>
              <div class="col-12 mt-5">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
